import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
  Search, Filter, ChevronDown, MoreVertical, Shield,
  Building2, Mail, Phone, MapPin, Calendar,
  Download, Trash2, Edit, Eye, X, CheckCircle, AlertTriangle,
  Users, ArrowUpDown, Ban, Lock, Unlock, AlertCircle,
  User, Globe, Clock, Languages, Star, Briefcase, DollarSign,
  CreditCard, FileText, BadgeCheck, Loader2
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { fetchContracts, updateContract } from "../../redux/Slices/contractSlice";
import { ContractStatusEnum } from "../../modules/shared/model/contract.enum";
import { RootState } from "../../redux/store";

// Extended contract interface for the API response
interface ExtendedContract {
  id: string;
  title: string;
  description: string;
  amount: number;
  status: { id: number; name: string };
  type: { id: number; name: string };
  professional: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    averageRating?: number;
    reviewsCount?: number;
    reviews?: any[];
  };
  client: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
  };
  createdAt: string;
  enableTracker: boolean;
  canAutomaticWithdraw: boolean;
  weeklyLimit: number;
}

const BusinessDirectory = () => {
  const dispatch = useDispatch();
  const { items: contracts, loading, totalCount, error } = useSelector((state: RootState) => state.contracts);
  
  // Cast contracts to extended interface
  const extendedContracts = contracts as unknown as ExtendedContract[];
  
  // State management
  const [selectedBusinesses, setSelectedBusinesses] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [businessType, setBusinessType] = useState("all");
  const [status, setStatus] = useState("all");
  const [showBusinessDetails, setShowBusinessDetails] = useState(null);
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [verification, setVerification] = useState("all");
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  // Load contracts on component mount and when filters change
  useEffect(() => {
    const queryParams: Record<string, string | number | boolean> = {
      page: currentPage,
      limit: 10,
    };

    if (searchQuery) queryParams.search = searchQuery;
    if (businessType !== "all") queryParams.type = businessType;
    if (status !== "all") queryParams.status = status;
    if (verification !== "all") queryParams.verified = verification;
    if (sortBy !== "newest") queryParams.sortBy = sortBy;

    dispatch(fetchContracts(queryParams) as any).then((result: any) => {
      if (result.payload && typeof result.payload === "object" && "hasNextPage" in result.payload) {
        setHasNextPage(result.payload.hasNextPage);
      }
    });
  }, [dispatch, currentPage, searchQuery, businessType, status, verification, sortBy]);

  // Handle contract status updates
  const handleStatusUpdate = async (contractId: string, newStatus: ContractStatusEnum) => {
    try {
      setUpdatingStatus(contractId);
      await dispatch(updateContract({ 
        id: contractId, 
        data: { 
          status: { id: newStatus } 
        } 
      }) as any);
      // Refresh the contracts list
      dispatch(fetchContracts({ page: currentPage, limit: 10 }) as any).then((result: any) => {
        if (result.payload && typeof result.payload === "object" && "hasNextPage" in result.payload) {
          setHasNextPage(result.payload.hasNextPage);
        }
      });
    } catch (error) {
      console.error("Failed to update contract status:", error);
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const queryParams: Record<string, string | number | boolean> = {
        page: 1,
        limit: 10,
      };

      if (searchQuery) queryParams.search = searchQuery;
      if (businessType !== "all") queryParams.type = businessType;
      if (status !== "all") queryParams.status = status;
      if (verification !== "all") queryParams.verified = verification;
      if (sortBy !== "newest") queryParams.sortBy = sortBy;

      dispatch(fetchContracts(queryParams) as any).then((result: any) => {
        if (result.payload && typeof result.payload === "object" && "hasNextPage" in result.payload) {
          setHasNextPage(result.payload.hasNextPage);
        }
      });
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, dispatch, businessType, status, verification, sortBy]);

  // Get status display info
  const getStatusInfo = (statusId: number) => {
    switch (statusId) {
      case ContractStatusEnum.Active:
        return { text: "Active", color: "bg-green-100 text-green-700", dot: "bg-green-500" };
      case ContractStatusEnum.Pending:
        return { text: "Pending", color: "bg-yellow-100 text-yellow-700", dot: "bg-yellow-500" };
      case ContractStatusEnum.Suspended:
        return { text: "Suspended", color: "bg-red-100 text-red-700", dot: "bg-red-500" };
      case ContractStatusEnum.Deleted:
        return { text: "Deleted", color: "bg-gray-100 text-gray-700", dot: "bg-gray-500" };
      case ContractStatusEnum.Completed:
        return { text: "Completed", color: "bg-blue-100 text-blue-700", dot: "bg-blue-500" };
      default:
        return { text: "Unknown", color: "bg-gray-100 text-gray-700", dot: "bg-gray-500" };
    }
  };

  // Business Details Modal Component
  const BusinessDetailsModal = ({ contract, onClose }) => {
    if (!contract) return null;

    const statusInfo = getStatusInfo(Number(contract.status.id));
    const professional = contract.professional;
    const client = contract.client;
    const offer = contract.offer;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          <div className="p-6 border-b sticky top-0 bg-white"  style={{ textAlign: "left" }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">{contract.title}</h2>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>ID: {contract.id}</span>
                    <span>•</span>
                    <Badge variant={undefined} className={statusInfo.color}>
                      {statusInfo.text}
                    </Badge>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Contract Information */}
            <div className="space-y-4">
              <h3 className="font-medium">Contract Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-500">Title</span>
                  <p className="font-medium">{contract.title}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Amount</span>
                  <p className="font-medium">${contract.amount}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Type</span>
                  <p className="font-medium">{contract.type.name}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Created</span>
                  <p className="font-medium">{new Date(contract.createdAt).toLocaleDateString()}</p>
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div className="space-y-4">
              <h3 className="font-medium">Professional Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-500">Name</span>
                  <p className="text-xl font-semibold">{professional.firstName} {professional.lastName}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-500">Email</span>
                  <p className="text-xl font-semibold">{professional.email}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-500">Phone</span>
                  <p className="text-xl font-semibold">{professional.phone}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-500">Location</span>
                  <p className="text-xl font-semibold">{professional.address}</p>
                </div>
              </div>
            </div>

            {/* Client Information */}
            <div className="space-y-4">
              <h3 className="font-medium">Client Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-500">Name</span>
                  <p className="text-xl font-semibold">{client.firstName} {client.lastName}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-500">Email</span>
                  <p className="text-xl font-semibold">{client.email}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-500">Phone</span>
                  <p className="text-xl font-semibold">{client.phone}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-500">Location</span>
                  <p className="text-xl font-semibold">{client.address}</p>
                </div>
              </div>
            </div>

            {/* Contract Details */}
            <div className="space-y-4">
              <h3 className="font-medium">Contract Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-500">Description</span>
                  <p className="text-sm">{contract.description}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-500">Weekly Limit</span>
                  <p className="text-xl font-semibold">{contract.weeklyLimit} hours</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-500">Tracker Enabled</span>
                  <p className="text-xl font-semibold">{contract.enableTracker ? "Yes" : "No"}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-500">Auto Withdraw</span>
                  <p className="text-xl font-semibold">{contract.canAutomaticWithdraw ? "Yes" : "No"}</p>
                </div>
              </div>
            </div>

            {/* Professional Reviews */}
            {professional.reviews && professional.reviews.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-medium">Professional Reviews</h3>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-gray-500">Average Rating:</span>
                    <div className="flex items-center">
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <span className="ml-1 font-medium">{professional.averageRating}</span>
                    </div>
                    <span className="text-sm text-gray-500">({professional.reviewsCount} reviews)</span>
                  </div>
                </div>
              </div>
            )}

            {/* Admin Actions */}
            <div className="space-y-4">
              <h3 className="font-medium">Admin Actions</h3>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Contact Professional
                </Button>
                <Button variant="outline" className="flex items-center gap-2">
                  <Edit className="h-4 w-4" />
                  Edit Contract
                </Button>
                {contract.status.id === ContractStatusEnum.Active ? (
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 text-yellow-600"
                    onClick={() => handleStatusUpdate(contract.id, ContractStatusEnum.Suspended)}
                    disabled={updatingStatus === contract.id}
                  >
                    {updatingStatus === contract.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Ban className="h-4 w-4" />
                    )}
                    Suspend Contract
                  </Button>
                ) : contract.status.id === ContractStatusEnum.Suspended ? (
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 text-green-600"
                    onClick={() => handleStatusUpdate(contract.id, ContractStatusEnum.Active)}
                    disabled={updatingStatus === contract.id}
                  >
                    {updatingStatus === contract.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4" />
                    )}
                    Activate Contract
                  </Button>
                ) : null}
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2 text-red-600"
                  onClick={() => handleStatusUpdate(contract.id, ContractStatusEnum.Deleted)}
                  disabled={updatingStatus === contract.id}
                >
                  {updatingStatus === contract.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                  Delete Contract
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col"  style={{ textAlign: "left" }}>
      <AdminHeader />
      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Business Directory</h1>
                <p className="text-gray-600 mt-1">Manage and monitor all registered businesses</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Export Directory
                </Button>
                <Button className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Add Business
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Contracts</p>
                    <p className="text-2xl font-semibold">{totalCount || 0}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Building2 className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Active Contracts</p>
                    <p className="text-2xl font-semibold">
                      {extendedContracts.filter(c => c.status.id === ContractStatusEnum.Active).length}
                    </p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Pending Contracts</p>
                    <p className="text-2xl font-semibold">
                      {extendedContracts.filter(c => c.status.id === ContractStatusEnum.Pending).length}
                    </p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Revenue</p>
                    <p className="text-2xl font-semibold">
                      ${extendedContracts.reduce((sum, c) => sum + (c.amount || 0), 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Main Content Card */}
            <Card className="overflow-hidden">
              {/* Search and Filters */}
              <div className="p-4 border-b">
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="relative flex-1 sm:min-w-[300px]">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search businesses..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg"
                      />
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setShowFilters(!showFilters)}
                      className="flex items-center gap-2"
                    >
                      <Filter className="h-5 w-5" />
                      Filters
                    </Button>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-4 py-2 border rounded-lg"
                    >
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                      <option value="revenue">Highest Revenue</option>
                      <option value="rating">Highest Rated</option>
                      <option value="jobs">Most Jobs</option>
                    </select>
                  </div>
                </div>

                <AnimatePresence>
                  {showFilters && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mt-4 pt-4 border-t"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Contract Type
                          </label>
                          <select
                            value={businessType}
                            onChange={(e) => setBusinessType(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg"
                          >
                            <option value="all">All Types</option>
                            <option value="1">Fixed</option>
                            <option value="2">Hourly</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Status
                          </label>
                          <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg"
                          >
                            <option value="all">All Status</option>
                            <option value="2">Active</option>
                            <option value="1">Pending</option>
                            <option value="6">Suspended</option>
                            <option value="4">Completed</option>
                            <option value="7">Deleted</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Verification
                          </label>
                          <select
                            value={verification}
                            onChange={(e) => setVerification(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg"
                          >
                            <option value="all">All</option>
                            <option value="true">Verified</option>
                            <option value="false">Unverified</option>
                          </select>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Contracts Table */}
              <div className="overflow-x-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                    <span className="ml-2 text-gray-500">Loading contracts...</span>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedBusinesses(extendedContracts.map(contract => contract.id));
                              } else {
                                setSelectedBusinesses([]);
                              }
                            }}
                          />
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contract
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Professional
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {extendedContracts.map((contract) => {
                        const statusInfo = getStatusInfo(Number(contract.status.id));
                        return (
                          <tr key={contract.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                checked={selectedBusinesses.includes(contract.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedBusinesses([...selectedBusinesses, contract.id]);
                                  } else {
                                    setSelectedBusinesses(selectedBusinesses.filter(id => id !== contract.id));
                                  }
                                }}
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                                  <Building2 className="h-5 w-5 text-gray-400" />
                                </div>
                                <div className="ml-4">
                                  <div className="font-medium text-gray-900">{contract.title}</div>
                                  <div className="text-sm text-gray-500">ID: {contract.id}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Badge variant={undefined} className={
                                contract.type.id === 1 
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-purple-100 text-purple-700"
                              }>
                                {contract.type.name}
                              </Badge>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className={`h-2.5 w-2.5 rounded-full mr-2 ${statusInfo.dot}`} />
                                <span>{statusInfo.text}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                              ${contract.amount}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                                  <User className="h-4 w-4 text-gray-400" />
                                </div>
                                <div className="ml-2">
                                  <div className="text-sm font-medium text-gray-900">
                                    {contract.professional.firstName} {contract.professional.lastName}
                                  </div>
                                  <div className="text-xs text-gray-500">{contract.professional.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setShowBusinessDetails(contract)}
                                  className="text-blue-600 hover:text-blue-700"
                                >
                                  View Details
                                </Button>
                                <div className="relative inline-block text-left">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">
                    Showing {extendedContracts.length} of {totalCount || 0} contracts
                  </span>
                  {totalCount && (
                    <span className="text-sm text-gray-500">
                      (Page {currentPage} of {Math.ceil(totalCount / 10)})
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || loading}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!hasNextPage || loading || (totalCount && currentPage >= Math.ceil(totalCount / 10))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </main>

      {/* Modals */}
      <AnimatePresence>
        {showBusinessDetails && (
          <BusinessDetailsModal
            contract={showBusinessDetails}
            onClose={() => setShowBusinessDetails(null)}
          />
        )}
      </AnimatePresence>

      {/* <SharedFooter3 /> */}
    </div>
  );
};

export default BusinessDirectory;